import React from "react";
import ReactMarkdown, { defaultUrlTransform } from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import rehypeRaw from "rehype-raw";
import "katex/dist/katex.min.css";
import { Link } from "@mui/material";
import * as S from "./style";

interface MarkdownContentProps {
  content: string;
}

const isHTML = (str: string): boolean => {
  return /<[^>]*>/g.test(str);
};

export default function MarkdownContent({ content }: MarkdownContentProps) {
  const plugins: any[] = [remarkGfm, remarkMath];
  const rehypePlugins: any[] = [rehypeKatex];
  if (isHTML(content)) {
    rehypePlugins.push(rehypeRaw);
  }

  return (
    <S.MarkdownBox>
      <ReactMarkdown
        remarkPlugins={plugins}
        rehypePlugins={rehypePlugins}
        // react-markdown's default urlTransform only allows http(s)/mailto/etc.,
        // so it strips `data:` image URIs (used by guideline figures stored as
        // inline base64), leaving a broken `<img alt="img-0.jpeg">`. Preserve
        // inline data-image URIs; defer everything else to the default sanitizer.
        urlTransform={(url) =>
          url.startsWith("data:image/") ? url : defaultUrlTransform(url)
        }
        components={{
          p: ({ children }) => (
            <S.ParagraphText variant="body2">
              {React.Children.toArray(children as any)}
            </S.ParagraphText>
          ),
          h1: ({ children }) => (
            <S.Heading1Text variant="h5">
              {React.Children.toArray(children as any)}
            </S.Heading1Text>
          ),
          h2: ({ children }) => (
            <S.Heading2Text variant="h6">
              {React.Children.toArray(children as any)}
            </S.Heading2Text>
          ),
          h3: ({ children }) => (
            <S.Heading3Text variant="subtitle1">
              {React.Children.toArray(children as any)}
            </S.Heading3Text>
          ),
          h4: ({ children }) => (
            <S.Heading4Text variant="subtitle2">
              {React.Children.toArray(children as any)}
            </S.Heading4Text>
          ),
          h5: ({ children }) => (
            <S.Heading5Text variant="body1">
              {React.Children.toArray(children as any)}
            </S.Heading5Text>
          ),
          h6: ({ children }) => (
            <S.Heading6Text variant="body2">
              {React.Children.toArray(children as any)}
            </S.Heading6Text>
          ),
          a: ({ href, children }) => (
            <Link href={href} target="_blank" rel="noopener noreferrer" underline="hover">
              {React.Children.toArray(children as any)}
            </Link>
          ),
          ul: ({ children }) => (
            <S.UnorderedList component="ul">
              {React.Children.toArray(children as any)}
            </S.UnorderedList>
          ),
          ol: ({ children, start }: any) => (
            <S.OrderedList component="ol" start={start}>
              {React.Children.toArray(children as any)}
            </S.OrderedList>
          ),
          li: ({ children, value }: any) => (
            <S.ListItemText component="li" variant="body2" value={value}>
              {React.Children.toArray(children as any)}
            </S.ListItemText>
          ),
          blockquote: ({ children }) => (
            <S.BlockQuoteText component="blockquote">
              {React.Children.toArray(children as any)}
            </S.BlockQuoteText>
          ),
          code: ({ inline, children, ...props }: any) =>
            inline ? (
              <S.InlineCodeText component="code">
                {React.Children.toArray(children as any)}
              </S.InlineCodeText>
            ) : (
              <S.CodeBlockText component="pre">
                <S.CodeBlockInner {...props}>
                  {React.Children.toArray(children as any)}
                </S.CodeBlockInner>
              </S.CodeBlockText>
            ),
          strong: ({ children }) => (
            <S.StrongText component="strong">
              {React.Children.toArray(children as any)}
            </S.StrongText>
          ),
          em: ({ children }) => (
            <S.EmText component="em">{React.Children.toArray(children as any)}</S.EmText>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </S.MarkdownBox>
  );
}
